import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, Video, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from 'react-i18next';

const DemoBooking = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  const bookingSchema = z.object({
    name: z.string().min(2, t('demo_booking.validation_name')),
    email: z.string().email(t('demo_booking.validation_email')),
    company: z.string().min(2, t('demo_booking.validation_company')),
    phone: z.string().optional(),
    preferredDate: z.string().min(1, t('demo_booking.validation_date')),
    preferredTime: z.string().min(1, t('demo_booking.validation_time')),
    attendees: z.string().optional(),
  });

  type BookingForm = z.infer<typeof bookingSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
  });

  const timeSlots = [
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
    "04:00 PM",
  ];

  const benefits = [
    t('demo_booking.benefit_1'),
    t('demo_booking.benefit_2'),
    t('demo_booking.benefit_3'),
    t('demo_booking.benefit_4')
  ];

  const onSubmit = async (data: BookingForm) => {
    setIsSubmitting(true);

    try {
      const response = await fetch("/contact-form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone || "",
          preferredDate: data.preferredDate,
          preferredTime: data.preferredTime,
          attendees: data.attendees || "",
          isBooking: true,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to book demo");
      }

      downloadCalendarInvite(data);

      toast({
        title: t('demo_booking.toast_success_title'),
        description: t('demo_booking.toast_success_description'),
      });

      setIsBooked(true);
      reset();
    } catch (error) {
      console.error(error);
      toast({
        title: t('demo_booking.toast_error_title'),
        description:
          error instanceof Error
            ? error.message
            : t('demo_booking.toast_error_description'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadCalendarInvite = (data: BookingForm) => {
    const meetingDurationMinutes = 45;
    const { start, end } = buildMeetingDates(data.preferredDate, data.preferredTime, meetingDurationMinutes);

    if (!start || !end) {
      console.warn("Unable to parse meeting date/time", data.preferredDate, data.preferredTime);
      return;
    }

    const description = [
      `Name: ${data.name}`,
      `Company: ${data.company}`,
      `Work Email: ${data.email}`,
      data.phone ? `Phone: ${data.phone}` : undefined,
      data.attendees ? `Additional Attendees: ${data.attendees}` : undefined,
      "Meeting Type: Holionix AI Platform Demo",
    ]
      .filter(Boolean)
      .join("\\n");

    const icsContent = createICSContent({
      start,
      end,
      summary: "Holionix AI Demo",
      description,
      location: "Microsoft Teams / Virtual",
    });

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `holionix-demo-${data.preferredDate}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const buildMeetingDates = (dateStr: string, timeLabel: string, durationMinutes: number) => {
    const [year, month, day] = dateStr.split("-").map((part) => Number(part));
    if (!year || !month || !day) {
      return { start: null, end: null };
    }

    const { hours, minutes } = parseTimeLabel(timeLabel);
    if (hours === null || minutes === null) {
      return { start: null, end: null };
    }

    const start = new Date(year, month - 1, day, hours, minutes, 0, 0);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    return { start, end };
  };

  const parseTimeLabel = (label: string) => {
    const trimmed = label.trim();
    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);

    if (!match) {
      return { hours: null as number | null, minutes: null as number | null };
    }

    let hours = Number(match[1]);
    const minutes = match[2] ? Number(match[2]) : 0;
    const meridiem = match[3]?.toUpperCase();

    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    }
    if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  };

  const createICSContent = ({
    start,
    end,
    summary,
    description,
    location,
  }: {
    start: Date;
    end: Date;
    summary: string;
    description: string;
    location: string;
  }) => {
    const formatDate = (date: Date) =>
      date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

    const uid = crypto.randomUUID?.() || `${Date.now()}@holionix`;

    return [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Holionix//Demo Booking//EN",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${formatDate(new Date())}`,
      `DTSTART:${formatDate(start)}`,
      `DTEND:${formatDate(end)}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
  };

  if (isBooked) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="card-dark gradient-border p-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
          </motion.div>
          <h3 className="text-3xl font-bold mb-4">{t('demo_booking.success_title')}</h3>
          <p className="text-foreground/70 mb-6 max-w-md mx-auto">
            {t('demo_booking.success_description')}
          </p>
          <Button 
            onClick={() => setIsBooked(false)}
            variant="outline"
            className="border-accent text-accent hover:bg-accent/10"
          >
            {t('demo_booking.book_another')}
          </Button>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <Card className="card-dark gradient-border p-8">
        <div className="flex items-center gap-3 mb-6">
          <Video className="w-6 h-6 text-accent" />
          <h3 className="text-2xl font-bold">{t('demo_booking.title')}</h3>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">{t('demo_booking.label_name')} *</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder={t('demo_booking.placeholder_name')}
              className="mt-1"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email">{t('demo_booking.label_email')} *</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder={t('demo_booking.placeholder_email')}
              className="mt-1"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="company">{t('demo_booking.label_company')} *</Label>
            <Input
              id="company"
              {...register("company")}
              placeholder={t('demo_booking.placeholder_company')}
              className="mt-1"
            />
            {errors.company && (
              <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="phone">{t('demo_booking.label_phone')}</Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              placeholder={t('demo_booking.placeholder_phone')}
              className="mt-1"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="preferredDate" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('demo_booking.label_date')} *
              </Label>
              <Input
                id="preferredDate"
                type="date"
                {...register("preferredDate")}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
              {errors.preferredDate && (
                <p className="text-red-500 text-sm mt-1">{errors.preferredDate.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="preferredTime" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t('demo_booking.label_time')} *
              </Label>
              <select
                id="preferredTime"
                {...register("preferredTime")}
                className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/20"
              >
                <option value="">{t('demo_booking.placeholder_time')}</option>
                {timeSlots.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              {errors.preferredTime && (
                <p className="text-red-500 text-sm mt-1">{errors.preferredTime.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="attendees">{t('demo_booking.label_attendees')}</Label>
            <Input
              id="attendees"
              {...register("attendees")}
              placeholder={t('demo_booking.placeholder_attendees')}
              className="mt-1"
            />
            <p className="text-xs text-foreground/60 mt-1">
              {t('demo_booking.attendees_hint')}
            </p>
          </div>

          <Button
            type="submit"
            className="w-full gradient-brand text-white hover:opacity-90 h-12 text-base"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('demo_booking.submitting') : t('demo_booking.submit_button')}
          </Button>
        </form>

        <p className="text-xs text-foreground/60 text-center mt-4">
          {t('demo_booking.terms_agree')}
        </p>
      </Card>

      <div className="space-y-6">
        <Card className="card-dark gradient-border p-8 bg-gradient-to-br from-gradient-pink/10 to-gradient-purple/10">
          <h3 className="text-xl font-bold mb-4">{t('demo_booking.what_to_expect_title')}</h3>
          <ul className="space-y-3">
            {benefits.map((benefit, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-foreground/80">{benefit}</span>
              </motion.li>
            ))}
          </ul>
        </Card>

        <Card className="card-dark gradient-border p-8">
          <h3 className="text-xl font-bold mb-4">{t('demo_booking.format_title')}</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{t('demo_booking.duration_label')}</p>
                <p className="text-sm text-foreground/70">{t('demo_booking.duration_value')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Video className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{t('demo_booking.format_label')}</p>
                <p className="text-sm text-foreground/70">{t('demo_booking.format_value')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{t('demo_booking.availability_label')}</p>
                <p className="text-sm text-foreground/70">{t('demo_booking.availability_value')}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="card-dark gradient-border p-6 text-center glow-effect">
          <p className="text-sm text-foreground/70 mb-2">{t('demo_booking.immediate_assistance')}</p>
          <Button variant="outline" className="border-accent text-accent hover:bg-accent/10">
            {t('demo_booking.contact_sales')}
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default DemoBooking;
